import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../UserManagement/authprovider";
import {
    Box,
    Button,
    Container,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    HStack,
    IconButton,
    Image,
    Link,
    Text,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import React from "react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { AdminNavButton } from "./AdminNavButton";

const navBtnStyle = {
    variant: "ghost" as const,
    color: "#4299E1",
    fontSize: "1rem",
    fontWeight: "500",
    _hover: { bg: "#AFE7FA", color: "#2B6CB0" },
    px: 4,
    py: 2,
};

function NavButtons({ isLoggedIn, userRole, onClose }: { isLoggedIn: boolean; userRole: string; onClose?: () => void }) {
    const navigate = useNavigate();
    const handleNav = (path: string) => {
        navigate(path);
        onClose?.();
    };

    if (!isLoggedIn) {
        return (
            <>
                <Button {...navBtnStyle} onClick={() => handleNav("/signup")}>Sign Up</Button>
                <Button {...navBtnStyle} onClick={() => handleNav("/signin")}>Sign In</Button>
            </>
        );
    }

    return (
        <>
            <Button {...navBtnStyle} onClick={() => handleNav("/account")}>Account</Button>
            <AdminNavButton />
            {userRole === 'developer' && (
                <>
                    <Button {...navBtnStyle} onClick={() => handleNav("/proxy-example")}>Proxy Demo</Button>
                    <Button {...navBtnStyle} onClick={() => handleNav("/api-keys")}>API Keys</Button>
                </>
            )}
        </>
    );
}

export const TopBar = () => {
    const navigate = useNavigate();
    const { logout, isLoggedIn, userEmail, userRole } = useAuth();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <>
            {/* Desktop TopBar */}
            <Box display={{ base: "none", md: "block" }} position="sticky" top="0" zIndex={1000} w="full" bgGradient="linear(to-b, blue.50, whiteAlpha.800)" backdropFilter="blur(10px)" borderBottomWidth="1px" borderBottomColor="blue.100">
                <Container maxW="6xl" py={4}>
                    <Flex align="center" justify="space-between" gap={4}>
                        <Flex align="center" gap={3} minW="260px">
                            <Link as={RouterLink} to="/" display="flex" alignItems="center" gap={3} _hover={{ textDecoration: "none" }}>
                                <Image src="logo_small.png" alt="Glacio Logo" h="40px" objectFit="contain" />
                                <Text fontSize="lg" fontWeight="semibold" color="gray.700">Developer</Text>
                            </Link>
                        </Flex>
                        <Flex flex="1" justify="center">
                            <HStack spacing={2} justify="center">
                                <NavButtons isLoggedIn={isLoggedIn} userRole={userRole} />
                            </HStack>
                        </Flex>
                        <HStack spacing={2} minW="260px" justifyContent="flex-end">
                            {isLoggedIn && (
                                <Button {...navBtnStyle} onClick={handleLogout}>Sign Out</Button>
                            )}
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            {/* Mobile TopBar */}
            <Box display={{ base: "block", md: "none" }} position="sticky" top="0" zIndex={1000} w="full" bgGradient="linear(to-b, blue.50, whiteAlpha.800)" backdropFilter="blur(10px)" borderBottomWidth="1px" borderBottomColor="blue.100">
                <Container maxW="6xl" py={2} px={2}>
                    <Flex align="center" justify="space-between" width="100%">
                        <IconButton aria-label="Open menu" icon={<HamburgerIcon />} onClick={onOpen} size="sm" variant="ghost" />

                        <Flex direction="column" alignItems="center" justifyContent="center" flex="1">
                            <Link as={RouterLink} to="/" display="flex" justifyContent="center" alignItems="center" gap={2} _hover={{ textDecoration: "none" }}>
                                <Image src="/Glacio-Logo-ColorFill_v2.png" alt="Glacio Logo" h="34px" objectFit="contain" />
                                <Text fontSize="md" fontWeight="semibold" color="gray.700">Developer</Text>
                            </Link>
                        </Flex>

                        <Box width="32px" />
                    </Flex>
                </Container>

                <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
                    <DrawerOverlay />
                    <DrawerContent>
                        <DrawerCloseButton />
                        <DrawerHeader />
                        <DrawerBody>
                            <VStack align="stretch" spacing={2}>
                                <NavButtons isLoggedIn={isLoggedIn} userRole={userRole} onClose={onClose} />
                                {isLoggedIn && (
                                    <Button {...navBtnStyle} onClick={() => { handleLogout(); onClose(); }}>Sign Out</Button>
                                )}
                            </VStack>
                        </DrawerBody>
                    </DrawerContent>
                </Drawer>
            </Box>
        </>
    );
};
